/**
 * What a Team Work session view works out from the messages, apart from how it draws: who wrote each one, the
 * questions asked and whether each is answered, whom the reader may answer for (the team's cover rules and who is
 * away), whether the reader may steer, and the words a refusal comes back in. Pure, so it is tested without a page.
 */

import { coversOf, type ConversationMessage, type ConversationRecord, type TeamDelegation, type TeamworkAsk } from '@aioe/shared';

export interface SessionEntry {
  id: string;
  seq: number;
  at: string;
  by?: { principal?: string; actor?: string };
  data: ConversationMessage;
}

export interface Question {
  ask: TeamworkAsk;
  /** The message that asked it. */
  messageId: string;
  askedBy: string;
  answer?: { messageId: string; by: string; byId?: string; forName?: string; text: string };
}

/** Who wrote a message, in words, and what kind of author it is. */
export function authorOf(m: ConversationMessage, fallbackPerson?: string): { kind: 'person' | 'agent' | 'system'; name: string; id?: string } {
  const a = m.author;
  const kind = a?.kind ?? (m.role === 'user' ? 'person' : m.role === 'system' ? 'system' : 'agent');
  const name = a?.name?.trim() || (kind === 'person' ? (fallbackPerson ?? 'Someone on the project') : kind === 'system' ? 'The workbench' : 'An agent');
  return { kind, name, ...(a?.id ? { id: a.id } : {}) };
}

/** Every question in the session, in order, each with its answer when one came. */
export function questionsIn(entries: SessionEntry[]): Question[] {
  const out = new Map<string, Question>();
  for (const e of entries) {
    const m = e.data;
    if (m.asks) out.set(m.asks.questionId, { ask: m.asks, messageId: m.id, askedBy: authorOf(m).name });
    if (m.intent === 'answer' && m.questionId) {
      const q = out.get(m.questionId);
      if (q && !q.answer) q.answer = { messageId: m.id, by: authorOf(m).name, ...(m.author?.id ? { byId: m.author.id } : {}), ...(m.answeredFor ? { forName: m.answeredFor.name } : {}), text: m.text };
    }
  }
  return [...out.values()];
}

/** "Answered by Dan for Ana", or "Answered by Ana". */
export function answeredWords(q: Question): string | undefined {
  if (!q.answer) return undefined;
  return q.answer.forName ? `Answered by ${q.answer.by} for ${q.answer.forName}` : `Answered by ${q.answer.by}`;
}

/** Whether the reader may answer the question: it names them, or they cover the person it names (always, or while that person is away). */
export function mayAnswer(q: Question, me: string | undefined, delegation: TeamDelegation | undefined, away: ReadonlySet<string>): boolean {
  if (!me || q.answer) return false;
  if (q.ask.to === me) return true;
  return coversOf(delegation, q.ask.to, away.has(q.ask.to)).includes(me);
}

/** Whether the reader may steer the session: its owner or one of its steerers. A session from before owners has none, and anyone may try. */
export function maySteer(rec: Pick<ConversationRecord, 'data'> | undefined, me: string | undefined): boolean {
  if (!rec || !me) return false;
  const owner = rec.data.owner?.id;
  return !owner || owner === me || (rec.data.steerers ?? []).includes(me);
}

/** Whether a message names the reader with @. */
export const mentionsMe = (m: ConversationMessage, me: string | undefined): boolean => Boolean(me && m.mentions?.some((x) => x.subjectId === me));

/** A list of names in words: "Ana", "Ana and Ben", "Ana, Ben and Cy". */
export function namesWords(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** What a refused post says, from the platform's answer: who may steer or answer, or who is not on the project. */
export function refusalWords(body: unknown, nameOf: (id: string) => string): string | undefined {
  const b = body as { error?: string; who?: Array<{ id: string; name: string }>; ids?: string[]; message?: string } | undefined;
  if (!b?.error) return undefined;
  const who = namesWords((b.who ?? []).map((w) => w.name));
  if (b.error === 'not_a_steerer') return who ? `Only ${who} can steer this session. Send it as a reply instead.` : 'You cannot steer this session. Send it as a reply instead.';
  if (b.error === 'not_asked') return who ? `This question is for ${who} to answer.` : 'This question is not yours to answer.';
  if (b.error === 'not_a_member') return `${namesWords((b.ids ?? []).map(nameOf)) || 'Someone you named'} ${(b.ids ?? []).length === 1 ? 'is' : 'are'} not on the project, so they cannot be mentioned or asked.`;
  if (b.error === 'unknown_question') return 'That question is no longer in the session.';
  if (b.error === 'owner_only') return 'Only the person who started this session can choose who steers it.';
  return b.message;
}

/** A new message id the way the workbench makes them: unique enough to be idempotent on a retry. */
export const newMessageId = (): string => `msg_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** The first line of a message, short, for a reply's preview. */
export const firstLine = (text: string, max = 140): string => {
  const line = text.split('\n').find((l) => l.trim()) ?? '';
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};
